import React, { useState, useEffect } from 'react';
import { Shirt, User, Check, AlertCircle, Printer, Download } from 'lucide-react';

import ScanBadgeButton from '../../../components/scan-badge-button/scan-badge-button';
import ManualBadgeNotice from '../../../components/manual-judge-badge/manual-judge-badge';

import { fetchJudgeRecord } from '../../../utils/mongoDataOperations';
import { checkInJudgeHelper } from '../../../utils/helpers';
import { printBadge } from '../../../utils/badgePrint';
import { generateLaserBadgeSVG, downloadSVG } from '../../../utils/laserBadge';

const CheckIn = ({ user }) => {
  const [verificationState, setVerificationState] = useState({
    nameVerified: false,
  });
  const [scannedReturnedID, setScannedReturnedID] = useState(null);
  const [scannedUserRecord, setScannedUserRecord] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [checkInIsLoading, setCheckInIsLoading] = useState(false);

  const judgesList = [
    'Parag Agrawal',
    'Travers Nisbet',
    'Shubham	Gupta',
    'Yang	Fan Yun',
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (scannedReturnedID) {
        try {
          setStatusMessage(null);
          setVerificationState({
            nameVerified: false,
          });

          const response = await fetchJudgeRecord(scannedReturnedID);

          setScannedUserRecord(response);
        } catch (error) {
          console.error('Error fetching judge record:', error);
        }
      }
    };
    fetchData();
  }, [scannedReturnedID]);

  useEffect(() => {
    if (statusMessage) {
      setVerificationState({
        nameVerified: false,
      });
    }
  }, [statusMessage]);

  const allVerified = Object.values(verificationState).every((value) => value);

  const InfoItem = ({ icon: Icon, label, value }) => (
    <div className='flex items-center mb-4 space-x-3'>
      <div className='bg-gray-100 flex h-8 items-center justify-center rounded-full w-8'>
        <Icon className='h-4 text-gray-600 w-4' />
      </div>
      <div>
        <p className='text-gray-500 text-sm'>{label}</p>
        <p className='font-medium'>{value || '...'}</p>
      </div>
    </div>
  );

  const VerificationCheckbox = ({ label, checked, onChange, instruction }) => (
    <div className='mb-4'>
      <label className='cursor-pointer flex items-start space-x-3'>
        <div className='flex h-5 items-center'>
          <input
            type='checkbox'
            checked={checked}
            onChange={onChange}
            className='border-gray-300 focus:ring-blue-500 h-4 rounded text-blue-600 w-4'
          />
        </div>
        <div className='flex flex-col'>
          <span className='font-medium'>{label}</span>
          <span className='text-gray-500 text-sm'>{instruction}</span>
        </div>
      </label>
    </div>
  );

  const StatusMessage = ({ type, text }) => (
    <div
      className={`flex items-center space-x-2 p-4 rounded-lg mb-4 ${
        type === 'success'
          ? 'bg-green-50 text-green-700'
          : 'bg-red-50 text-red-700'
      }`}>
      {type === 'success' ? (
        <Check className='h-5 w-5' />
      ) : (
        <AlertCircle className='h-5 w-5' />
      )}
      <span>{text}</span>
    </div>
  );

  return (
    <div className='max-w-4xl mx-auto p-6'>
      <div className='flex flex-col mb-6'>
        <div className='flex items-center justify-between'>
          <div className='flex gap-4 items-center'>
            <h1 className='font-bold text-3xl'>Judge Check-In</h1>
          </div>
          <div className='flex items-center space-x-3'>
            <button
              onClick={() => {
                if (!scannedReturnedID || !scannedUserRecord) return;
                const firstName = scannedUserRecord?.application_info?.first_name || '';
                const lastName = scannedUserRecord?.application_info?.last_name || '';
                printBadge(firstName, lastName, scannedReturnedID);
              }}
              disabled={!scannedReturnedID || !scannedUserRecord}
              className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                scannedReturnedID && scannedUserRecord
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}>
              <Printer className='h-4 w-4' />
              <span>Print Badge</span>
            </button>
            <button
              onClick={async () => {
                if (!scannedReturnedID || !scannedUserRecord) return;
                const firstName = scannedUserRecord?.application_info?.first_name || '';
                const lastName = scannedUserRecord?.application_info?.last_name || '';
                const qrUrl = `https://trunk.treehacks.com/p/${scannedReturnedID}`;
                const svg = await generateLaserBadgeSVG(firstName, lastName, qrUrl);
                const fileName = `badge-${firstName.toLowerCase()}${lastName ? `-${lastName.toLowerCase()}` : ''}.svg`;
                downloadSVG(svg, fileName);
              }}
              disabled={!scannedReturnedID || !scannedUserRecord}
              className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                scannedReturnedID && scannedUserRecord
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}>
              <Download className='h-4 w-4' />
              <span>Laser Badge</span>
            </button>
            <button
              onClick={() => {
                setScannedUserRecord(null);
                setScannedReturnedID(null);
              }}
              className='border border-gray-300 hover:bg-gray-50 inline-flex items-center px-4 py-2 rounded-lg text-gray-700 transition-colors'>
              Clear
            </button>
            <ScanBadgeButton
              scannedReturnedID={scannedReturnedID}
              setScannedReturnedID={setScannedReturnedID}
            />
          </div>
        </div>
      </div>

      {statusMessage && (
        <StatusMessage
          type={statusMessage.type}
          text={statusMessage.text}
        />
      )}

      <div className='gap-6 grid grid-cols-1 md:grid-cols-2'>
        <div className='bg-white p-6 rounded-lg shadow'>
          <h2 className='font-semibold mb-4 text-xl'>Judge Info</h2>
          <InfoItem
            icon={User}
            label='Full Name'
            value={
              scannedUserRecord?.application_info
                ? scannedUserRecord?.application_info?.first_name +
                  ' ' +
                  scannedUserRecord?.application_info?.last_name
                : '...'
            }
          />
          <InfoItem
            icon={Shirt}
            label='T-Shirt Size'
            value={scannedUserRecord?.check_in_info?.tshirtSize || '...'}
          />
        </div>

        {/* Verification Card or Already Checked In Message */}
        <div className='bg-white p-6 rounded-lg shadow'>
          {scannedUserRecord?.check_in_info?.checkedIn ? (
            <div className='flex flex-col h-full items-center justify-center text-center'>
              <div className='bg-yellow-50 mb-4 p-4 rounded-full'>
                <AlertCircle className='h-12 text-yellow-500 w-12' />
              </div>
              <h2 className='font-semibold mb-2 text-2xl'>
                Already Checked In
              </h2>
              <p className='text-gray-600'>
                This judge has already completed the check-in process.
              </p>
            </div>
          ) : (
            <>
              <h2 className='font-semibold mb-4 text-xl'>ID Verification</h2>

              {/* Government ID Section */}
              <div className='mb-6'>
                <div className='flex items-center mb-3'>
                  <div className='bg-blue-600 h-6 mr-2 rounded w-1'></div>
                  <h3 className='font-medium text-gray-900'>
                    Government ID Verification
                  </h3>
                </div>

                <div className='border-gray-100 border-l-2 pl-3'>
                  <p className='mb-3 text-red-600 text-sm'>
                    Government ID should be a legal ID like a driver's license
                    or passport.
                  </p>
                  <VerificationCheckbox
                    label='Name Verification'
                    checked={verificationState.nameVerified}
                    onChange={(e) =>
                      setVerificationState((prev) => ({
                        ...prev,
                        nameVerified: e.target.checked,
                      }))
                    }
                    instruction='Confirm name matches government ID'
                  />
                </div>
              </div>

              <button
                onClick={() => {
                  setCheckInIsLoading(true);
                  checkInJudgeHelper(
                    scannedReturnedID,
                    user,
                    setStatusMessage,
                    setCheckInIsLoading,
                    scannedUserRecord
                  );
                }}
                disabled={
                  !allVerified || scannedReturnedID === null || checkInIsLoading
                }
                className={`w-full py-3 px-4 rounded-lg font-medium text-white text-lg mt-4
                  ${
                    allVerified &&
                    scannedReturnedID !== null &&
                    !checkInIsLoading
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}>
                Check In
              </button>

              <p className='flex items-center mt-4 text-red-600 text-sm'>
                <AlertCircle className='h-5 inline mr-2 w-5' />
                If any info doesn't match their IDs, please direct the judge to
                an exec on shift.
              </p>
            </>
          )}
        </div>
      </div>

      <ManualBadgeNotice judges={judgesList} />
    </div>
  );
};

export default CheckIn;
